import React, { useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useFAQs, useReorderFAQs } from "./hooks/faqs.hooks";
import { useHotel } from "@/hooks/useHotel";
import { usePageSize } from "@/hooks/usePageSize";
import { Button } from "@/components/ui/button";
import { FAQItem } from "./FAQItem";
import { CreateFAQItem } from "./CreateFAQItem";
import { cn } from "@/lib/utils";

interface FAQListSectionProps {
  categoryId: string;
  className?: string;
}

export const FAQListSection: React.FC<FAQListSectionProps> = ({
  categoryId,
  className,
}) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const [page, setPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const limit = usePageSize(listRef, {
    rowSelector: "[data-page-row]",
    gap: 8,
    min: 3,
    max: 30,
    fallback: 7,
  });

  const [prevKey, setPrevKey] = useState(`${limit}:${categoryId}`);
  if (prevKey !== `${limit}:${categoryId}`) {
    setPrevKey(`${limit}:${categoryId}`);
    setPage(1);
  }
  const [knownTotalPages, setKnownTotalPages] = useState(1);

  const { data, isLoading } = useFAQs({
    categoryId,
    page,
    limit,
  });

  if (data && data.totalPages !== knownTotalPages) {
    setKnownTotalPages(data.totalPages);
  }

  const reorderFAQs = useReorderFAQs();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && data?.items && hotelId) {
      const oldIndex = data.items.findIndex((item) => item.id === active.id);
      const newIndex = data.items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(data.items, oldIndex, newIndex);

      reorderFAQs.mutate({
        ids: newItems.map((item) => item.id),
        hotelId,
        type: "FAQ",
        startIndex: (page - 1) * limit,
      });
    }
  };

  return (
    <div
      className={cn(
        "border-base-border relative flex h-full flex-col gap-4 rounded-xl border bg-white p-4 shadow-sm",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-black/96">{t("FAQs")}</h2>
        <Button
          variant="new_default"
          onClick={() => setIsCreating(true)}
          disabled={isCreating}
        >
          <Plus />
          {t("Create FAQ")}
        </Button>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            {t("Loading...")}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={data?.items.map((i) => i.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-2">
                {isCreating && (
                  <CreateFAQItem
                    categoryId={categoryId}
                    onCancel={() => setIsCreating(false)}
                  />
                )}
                {data?.items.map((faq) => (
                  <div key={faq.id} data-page-row>
                    <FAQItem faq={faq} />
                  </div>
                ))}
                {!isLoading && data?.items.length === 0 && !isCreating && (
                  <div className="text-muted-foreground py-8 text-center">
                    {t("No FAQs found for this category")}
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <Pagination
        className={cn(
          "border-base-border mt-auto border-t pt-4",
          knownTotalPages <= 1 && "invisible",
        )}
      >
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              className={cn(
                "cursor-pointer",
                page === 1 && "pointer-events-none opacity-50",
              )}
              onClick={() => setPage(Math.max(1, page - 1))}
            />
          </PaginationItem>
          {data &&
            Array.from({ length: data.totalPages }, (_, i) => i + 1).map(
              (p) => (
                <PaginationItem key={p}>
                  <PaginationLink
                    isActive={page === p}
                    onClick={() => setPage(p)}
                    className="cursor-pointer"
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
          <PaginationItem>
            <PaginationNext
              className={cn(
                "cursor-pointer",
                (!data || page === data.totalPages) &&
                  "pointer-events-none opacity-50",
              )}
              onClick={() => setPage(page + 1)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
